import { useMemo, useRef, useState, useEffect } from 'react'
import { Search, ChevronDown, Check, Briefcase } from 'lucide-react'
import { Project } from '../services/projects'
import { useTranslation } from '../i18n/I18nContext'

interface ProjectPickerProps {
  projects: Project[]
  value: number
  onChange: (projectId: number) => void
  disabled?: boolean
  required?: boolean
}

// Group key per project status — domain-neutral (status always present), so a
// long flat list collapses into scannable sections instead of ~100 flat rows.
function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Searchable, status-grouped project picker (KQA-8).
 *
 * Replaces the ~100-item flat <select> on the task form. Filters by name as you
 * type and groups results by status. Styling reuses the established slate
 * palette already used across task/project forms (no new color values).
 */
export function ProjectPicker({ projects, value, onChange, disabled }: ProjectPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const selected = projects.find((p) => p.id === value)

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? projects.filter((p) => p.name.toLowerCase().includes(q))
      : projects
    const groups = new Map<string, Project[]>()
    for (const p of filtered) {
      const key = statusLabel(p.status)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(p)
    }
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [projects, query])

  const pick = (id: number) => {
    onChange(id)
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-left text-[#2A3B4C] flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="flex items-center gap-2 truncate">
          <Briefcase className="w-4 h-4 text-[#486581] flex-shrink-0" />
          {selected ? (
            <span className="truncate">{selected.name}</span>
          ) : (
            <span className="text-[#829ab1]">{t('task.project.placeholder')}</span>
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-[#829ab1] flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border-2 border-[#bcccdc] rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-[#bcccdc] flex items-center gap-2">
            <Search className="w-4 h-4 text-[#829ab1]" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('task.project.search')}
              className="w-full text-sm outline-none text-[#2A3B4C]"
            />
          </div>
          <div className="overflow-y-auto">
            {grouped.length === 0 && (
              <div className="px-4 py-3 text-sm text-[#829ab1]">{t('task.project.noResults')}</div>
            )}
            {grouped.map(([group, items]) => (
              <div key={group}>
                <div className="px-3 py-1 text-xs font-medium text-[#829ab1] bg-[#f0f4f8] sticky top-0">
                  {group}
                </div>
                <ul>
                  {items.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => pick(p.id)}
                        className="w-full text-left px-4 py-2 text-sm text-[#2A3B4C] hover:bg-[#f0f4f8] flex items-center justify-between"
                      >
                        <span className="truncate">{p.name}</span>
                        {value === p.id && <Check className="w-4 h-4 text-[#486581] flex-shrink-0" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
